import { useEffect, useRef, type RefObject } from "react";

/**
 * The pinned last column's edge line (`.grid-pinned-last` in `client/src/index.css`, fixes waves 4
 * and 4b). The 1 px inset line on the pinned cells' left marks the edge the other columns scroll
 * under, so it is drawn only while that happens: the grid's region is wider inside than out and
 * is not scrolled to its right edge. Everywhere else (a pane wide enough for the table, or
 * scrolled all the way) there is no line.
 *
 * The attribute is set on the grid's scroll region itself, from its `scroll` event and a resize
 * observer on the region and its table (a pane that narrows, rows that load), not through React
 * state: a scroll never re-renders the grid.
 */
/**
 * The contract for a grid with a pinned last column (#1071, shared by Tenants and the lists that
 * follow): the pinned column is narrow, one primary action as a button plus a `SkyMenu` for the
 * rest, never a row of buttons (Users is the stated exception, #1135: a column stacks one labelled button per grant, because each acts on a different grant and a menu would hide which); the grid's pixel floor (`pixelColumnLayout`) is not wider than the
 * pane at 1024 px (927, the nav is its 48 px rail up to 1024 px), so no value sits under the pinned
 * column at 1024 px; with the 260 px drawer open (from 1025 px) the pane is the viewport less 309,
 * so a floor above 716 scrolls from 1025 px until the viewport is the floor plus 309 (1236 px for
 * 927). Below the floor the others scroll under it and the edge line shows. Every action stays a real button or menu
 * item, reachable by keyboard, with the label and disabled reason it had. Test it with
 * `columnsUnderPinned` in `client/src/tests/gridLayout.ts`.
 */
const SCROLLS_UNDER_ATTRIBUTE = "data-scrolls-under";

/** Whether columns pass under a pinned last column: the region scrolls and is not at its end. */
function scrollsUnder(region: HTMLElement): boolean {
  return region.scrollWidth - region.clientWidth - region.scrollLeft > 1;
}

/**
 * Keeps `data-scrolls-under` on the `SkyDataGrid` region inside the returned ref's element while
 * `enabled`. The ref goes on an element that holds the grid (the library forwards no ref).
 */
export function usePinnedColumnEdge<T extends HTMLElement>(enabled: boolean): RefObject<T | null> {
  const holder = useRef<T | null>(null);
  useEffect(() => {
    const region = holder.current?.querySelector<HTMLElement>('[role="region"]');
    if (!enabled || !region) return undefined;
    const update = () => region.toggleAttribute(SCROLLS_UNDER_ATTRIBUTE, scrollsUnder(region));
    update();
    region.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(region);
    const table = region.querySelector("table");
    if (table) observer.observe(table);
    return () => {
      region.removeEventListener("scroll", update);
      observer.disconnect();
      region.removeAttribute(SCROLLS_UNDER_ATTRIBUTE);
    };
  }, [enabled]);
  return holder;
}
